import React, { useContext } from 'react';
import { AppContext } from '../../App';
import { MapPin, TrendingUp } from 'lucide-react';

const RegionalHeatmap = () => {
  const { dashboardData } = useContext(AppContext);
  
  if (!dashboardData) return null;
  
  const { provincial_opportunities } = dashboardData;

  const getDemandColor = (score) => {
    if (score >= 85) return { bg: 'bg-[#22C55E]', text: 'text-[#22C55E]' };
    if (score >= 70) return { bg: 'bg-[#3B82F6]', text: 'text-[#3B82F6]' };
    if (score >= 55) return { bg: 'bg-[#F97316]', text: 'text-[#F97316]' };
    return { bg: 'bg-[#EF4444]', text: 'text-[#EF4444]' };
  };

  const getGrowthBadge = (potential) => {
    if (potential === 'High') return 'bg-[#22C55E]/20 text-[#22C55E]';
    if (potential === 'Medium') return 'bg-[#F97316]/20 text-[#F97316]';
    return 'bg-[#A1A1AA]/20 text-[#A1A1AA]';
  };

  // Sort by demand score for visual hierarchy
  const sortedOpportunities = [...provincial_opportunities].sort((a, b) => b.demand_score - a.demand_score);

  return (
    <div className="dashboard-card p-6" data-testid="regional-heatmap">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#22C55E]/10 rounded-lg">
            <MapPin className="w-5 h-5 text-[#22C55E]" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Regional Market Opportunities</h3>
            <p className="text-sm text-[#A1A1AA]">Top vehicle opportunities by province</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4 text-xs">
          <span className="text-[#A1A1AA]">Demand Score:</span>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-[#22C55E]" />
              <span className="text-[#A1A1AA]">85+</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-[#3B82F6]" />
              <span className="text-[#A1A1AA]">70-84</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-[#F97316]" />
              <span className="text-[#A1A1AA]">55-69</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-[#EF4444]" />
              <span className="text-[#A1A1AA]">&lt;55</span>
            </div>
          </div>
        </div>
      </div>

      {/* Heatmap Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {sortedOpportunities.map((opp, index) => {
          const demandStyle = getDemandColor(opp.demand_score);
          
          return (
            <div 
              key={index}
              className="relative p-4 bg-[#27272A]/50 rounded-lg border border-[#27272A] hover:border-[#3B82F6]/50 transition-all group"
              data-testid={`province-${opp.province}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-lg font-bold text-white">{opp.province}</span>
                <div className={`w-3 h-3 rounded-full ${demandStyle.bg}`} />
              </div>
              <p className="text-xs text-[#A1A1AA] mb-3 truncate">{opp.top_opportunity}</p>
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#52525B]">Demand</span>
                  <span className={`font-mono font-medium ${demandStyle.text}`}>{opp.demand_score}%</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#52525B]">Supply</span>
                  <span className={`font-mono ${opp.days_supply < 20 ? 'text-[#EF4444]' : 'text-white'}`}>
                    {opp.days_supply}d
                  </span>
                </div>
              </div>
              
              {/* Hover detail */}
              <div className="absolute inset-0 bg-[#18181B] rounded-lg p-4 opacity-0 group-hover:opacity-100 transition-opacity z-10 flex flex-col justify-center">
                <p className="text-xs text-[#A1A1AA] mb-1">{opp.province_name}</p>
                <p className="text-sm text-white font-medium mb-2">{opp.top_opportunity}</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-[#52525B]">Avg Price</span>
                    <p className="text-white font-mono">${(opp.avg_price/1000).toFixed(0)}K</p>
                  </div>
                  <div>
                    <span className="text-[#52525B]">Inventory</span>
                    <p className="text-white font-mono">{opp.inventory_count.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Top Opportunities Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#52525B] border-b border-[#27272A]">
              <th className="pb-3 font-medium">Province</th>
              <th className="pb-3 font-medium">Top Opportunity</th>
              <th className="pb-3 font-medium text-right">Demand</th>
              <th className="pb-3 font-medium text-right">Days Supply</th>
              <th className="pb-3 font-medium text-right">Avg Price</th>
              <th className="pb-3 font-medium text-center">Growth</th>
            </tr>
          </thead>
          <tbody>
            {sortedOpportunities.slice(0, 5).map((opp, index) => {
              const demandStyle = getDemandColor(opp.demand_score);
              
              return (
                <tr 
                  key={index}
                  className="border-b border-[#27272A]/50 hover:bg-white/5 transition-colors"
                >
                  <td className="py-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${demandStyle.bg}`} />
                      <span className="text-white font-medium">{opp.province_name}</span>
                    </div>
                  </td>
                  <td className="py-3 text-white">{opp.top_opportunity}</td>
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-12 h-1.5 bg-[#27272A] rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${demandStyle.bg}`}
                          style={{ width: `${opp.demand_score}%` }}
                        />
                      </div>
                      <span className="text-white font-mono text-xs w-8">{opp.demand_score}%</span>
                    </div>
                  </td>
                  <td className={`py-3 text-right font-mono ${opp.days_supply < 20 ? 'text-[#EF4444]' : 'text-white'}`}>
                    {opp.days_supply}d
                  </td>
                  <td className="py-3 text-right text-white font-mono">${opp.avg_price.toLocaleString()}</td>
                  <td className="py-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getGrowthBadge(opp.growth_potential)}`}>
                      {opp.growth_potential}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RegionalHeatmap;
